import { useMemo, useState, useEffect } from "react";
import {
  Plus,
  Search,
  MoreVertical,
  Calendar,
  Trash2,
  Edit,
  FileText,
  X,
  BookOpen,
  ClipboardCheck,
  Check,
} from "lucide-react";
import ActivityCheckIn from "./ActivityCheckIn";
import { useAuth } from "../../../app/AuthContext";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";
import {
  type ActivityData as ApiActivityData,
  type ClassData,
} from "../../../lib/api";
import {
  useActivities,
  useCreateActivity,
  useUpdateActivity,
  useDeleteActivity,
} from "../../../hooks/useActivities";
import { useClasses } from "../../../hooks/useClasses";
import { useDisciplines } from "../../../hooks/useDisciplines";

// Map frontend ActivityData to match backend + local needs if any
interface ActivityData extends ApiActivityData {
  class?: string; // Backend uses class_id, frontend uses class name for display
  school?: string; // Add if backend supports it or derived
}

interface ActivitiesManagerProps {
  initialFilter?: string | null;
  classes?: ClassData[];
}

export default function ActivitiesManager({
  initialFilter,
  classes: propsClasses,
}: ActivitiesManagerProps) {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();

  // React Query Hooks
  const { data: activitiesData = [], isLoading: activitiesLoading } =
    useActivities(token, activeProfile?.id);
  // Fetch classes if not provided via props (using same cache key as ClassesManager)
  const { data: fetchedClasses = [], isLoading: classesLoading } = useClasses(
    token,
    activeProfile?.id,
  );

  // Use props classes if available, otherwise use fetched classes
  const classes = propsClasses || fetchedClasses;

  // Mutations
  const createActivityMutation = useCreateActivity(token!, activeProfile?.id);
  const updateActivityMutation = useUpdateActivity(token!, activeProfile?.id);
  const deleteActivityMutation = useDeleteActivity(token!, activeProfile?.id);

  const loading = activitiesLoading || (!propsClasses && classesLoading);

  // Enrich activities with class names (memoized)
  const activities = useMemo(() => {
    return activitiesData.map((act) => {
      const cls = classes.find((c) => c.id === act.class_id);
      return {
        ...act,
        class: cls ? cls.name : "Turma Desconhecida",
        subject: act.subject || cls?.subject || "",
        school: activeProfile?.name || "Sem escola",
      };
    });
  }, [activitiesData, classes, activeProfile?.name]);

  const [searchTerm, setSearchTerm] = useState(initialFilter || "");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<ActivityData | null>(
    null,
  );
  const [viewingActivity, setViewingActivity] = useState<ActivityData | null>(
    null,
  );
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);

  const { data: disciplines = [], createDiscipline } = useDisciplines(
    token,
    activeProfile?.id,
  );

  // Combine disciplines with subjects from classes and activities to avoid duplicates
  const allAvailableSubjects = useMemo(() => {
    const fromDisciplines = disciplines.map((d) => d.name);
    const fromClasses = classes.map((c) => c.subject).filter(Boolean);
    const fromActivities = activitiesData.map((a) => a.subject).filter(Boolean);
    const combined = [...fromDisciplines, ...fromClasses, ...fromActivities];
    // Unique values, case-insensitive normalization for comparison
    return Array.from(new Set(combined.map((s) => s!.trim())));
  }, [disciplines, classes, activitiesData]);

  useEffect(() => {
    if (initialFilter) {
      setSearchTerm(initialFilter);
    }
  }, [initialFilter]);

  // Form State
  const [formData, setFormData] = useState<{
    title: string;
    type: string;
    subject: string;
    class_id: string;
    dueDate: string;
    status: "scheduled" | "realized";
  }>({
    title: "",
    type: "homework",
    subject: "",
    class_id: "",
    dueDate: "",
    status: "scheduled",
  });

  const filteredActivities = activities.filter(
    (a) =>
      a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.class && a.class.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (a.school && a.school.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const handleOpenModal = (activity?: ActivityData) => {
    if (activity) {
      setEditingActivity(activity);
      setFormData({
        title: activity.title,
        type: activity.type,
        subject: activity.subject || "",
        class_id: activity.class_id,
        dueDate: activity.due_date?.split("T")[0] ?? "",
        status:
          (activity as any).status === "completed" ? "realized" : "scheduled",
      });
    } else {
      setEditingActivity(null);
      setFormData({
        title: "",
        type: "homework",
        subject: "",
        class_id: "",
        dueDate: "",
        status: "scheduled",
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!token || !activeProfile) return;

    try {
      // Auto-create subject if it doesn't exist in our disciplines list
      if (formData.subject) {
        const subjectExists = disciplines.some(
          (d) => d.name.toLowerCase() === formData.subject.trim().toLowerCase(),
        );
        if (!subjectExists) {
          try {
            await createDiscipline({
              name: formData.subject.trim(),
              school_profile_id: activeProfile.id,
            });
          } catch (err) {
            console.error("Erro ao criar matéria automaticamente:", err);
            // Continue anyway, the activity will still have the subject string
          }
        }
      }

      const payload = {
        title: formData.title,
        type: formData.type,
        subject: formData.subject,
        school: activeProfile?.name || "Escola Padrão",
        class_id: formData.class_id,
        due_date: formData.dueDate
          ? new Date(formData.dueDate).toISOString()
          : formData.status === "realized"
            ? new Date().toISOString()
            : undefined,
        status: formData.status === "realized" ? "completed" : "active",
        school_profile_id: activeProfile?.id,
      };

      if (editingActivity) {
        await updateActivityMutation.mutateAsync({
          id: editingActivity.id,
          data: payload,
        });
      } else {
        const created = await createActivityMutation.mutateAsync(
          payload as any,
        );
        // If completed, we can open the check-in or something similar
        if (formData.status === "realized") {
          // After creating, if it is completed, open the check-in automatically
          setViewingActivity({
            ...created,
            class:
              classes.find((c) => c.id === created.class_id)?.name ||
              "Turma Desconhecida",
            school: activeProfile?.name || "Sem escola",
          } as any);
          setIsCheckInModalOpen(true);
        }
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to save activity", err);
      alert("Erro ao salvar atividade.");
    }
  };

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (window.confirm("Tem certeza que deseja excluir esta atividade?")) {
      try {
        await deleteActivityMutation.mutateAsync(id);
      } catch (err) {
        console.error("Failed to delete activity", err);
        alert("Erro ao excluir atividade.");
      }
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "exam":
        return "Prova";
      case "homework":
        return "Tarefa";
      case "project":
        return "Trabalho";
      case "review":
        return "Revisão";
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "exam":
        return "text-red-600 bg-red-50";
      case "homework":
        return "text-blue-600 bg-blue-50";
      case "project":
        return "text-purple-600 bg-purple-50";
      case "review":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  if (loading && activities.length === 0) {
    return <div className="p-6 text-center">Carregando atividades...</div>;
  }

  return (
    <div
      className="bg-(--card-bg) rounded-xl shadow-sm border"
      style={{ borderColor: "var(--border-color)" }}
    >
      <div
        className="p-6 border-b"
        style={{ borderColor: "var(--border-color)" }}
      >
        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="relative w-full md:w-96">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
              style={{ color: "var(--text-tertiary)" }}
            />
            <input
              type="text"
              placeholder="Buscar atividade, turma ou escola..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition bg-(--input-bg) text-(--text-primary)"
              style={{ borderColor: "var(--border-color)" }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-6 py-2 rounded-lg text-white font-medium hover:shadow-md transition w-full md:w-auto justify-center bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="w-5 h-5" />
            Nova Atividade
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Activities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredActivities.map((activity) => (
            <div
              key={activity.id}
              className="bg-(--card-bg) rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition group"
              style={{ borderColor: "var(--border-color)" }}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${getTypeColor(
                      activity.type,
                    )}`}
                  >
                    {getTypeLabel(activity.type)}
                  </span>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <button
                      onClick={() => handleOpenModal(activity)}
                      className="p-2 hover:bg-(--bg-accent) rounded-full transition"
                    >
                      <Edit className="w-4 h-4 text-(--text-tertiary)" />
                    </button>
                    <button
                      onClick={() => handleDelete(activity.id)}
                      className="p-2 hover:bg-red-50 rounded-full transition"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                <h3
                  className="text-lg font-bold mb-1"
                  style={{ color: "var(--text-primary)" }}
                >
                  {activity.title}
                </h3>
                <p className="text-sm text-(--text-tertiary) mb-4">
                  {activity.subject || "Sem matéria"}
                </p>

                <div className="space-y-3 text-sm text-(--text-tertiary) border-t pt-4">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-(--text-tertiary)" />
                    <span className="font-medium text-(--text-primary)">
                      {activity.school}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pl-6">
                    <span className="bg-(--bg-accent) px-2 py-0.5 rounded text-xs text-(--text-secondary)">
                      {activity.class || "Sem turma"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-(--text-tertiary)" />
                    <span>
                      Entrega:{" "}
                      {activity.due_date
                        ? new Date(activity.due_date).toLocaleDateString()
                        : "Sem data"}
                    </span>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setViewingActivity(activity)}
                    className="text-sm font-medium text-orange-600 hover:text-orange-700 hover:underline flex items-center gap-1"
                  >
                    Ver Detalhes <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredActivities.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-(--bg-accent) rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-(--text-tertiary)" />
            </div>
            <h3 className="text-xl font-medium text-(--text-primary) mb-2">
              Nenhuma atividade encontrada
            </h3>
            <p className="text-(--text-tertiary)">
              Tente buscar por outro termo ou crie uma nova atividade.
            </p>
          </div>
        )}
      </div>

      {/* Modal - View Activity Details */}
      {viewingActivity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-(--card-bg) rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">
            <div
              className="px-6 py-4 border-b flex justify-between items-center shrink-0"
              style={{ borderColor: "var(--border-color)" }}
            >
              <h3 className="text-lg font-semibold text-(--text-primary)">
                Detalhes da Atividade
              </h3>
              <button
                onClick={() => setViewingActivity(null)}
                className="text-(--text-tertiary) hover:text-(--text-secondary)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-(--text-primary) mb-1">
                    {viewingActivity.title}
                  </h2>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold inline-block ${getTypeColor(
                      viewingActivity.type,
                    )}`}
                  >
                    {getTypeLabel(viewingActivity.type)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-(--bg-secondary) p-4 rounded-lg">
                  <span className="text-xs text-(--text-tertiary) uppercase font-bold block mb-1">
                    Matéria
                  </span>
                  <span className="text-(--text-primary) font-medium">
                    {viewingActivity.subject || "Sem matéria"}
                  </span>
                </div>
                <div className="bg-(--bg-secondary) p-4 rounded-lg">
                  <span className="text-xs text-(--text-tertiary) uppercase font-bold block mb-1">
                    Data de Entrega
                  </span>
                  <span className="text-(--text-primary) font-medium flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {viewingActivity.due_date
                      ? new Date(viewingActivity.due_date).toLocaleDateString(
                          "pt-BR",
                        )
                      : "Sem data"}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => setIsCheckInModalOpen(true)}
                  className="w-full flex items-center justify-center gap-2 p-3 text-white rounded-lg shadow-md transition-all font-bold hover:opacity-90 active:scale-95"
                  style={{ backgroundColor: "var(--success-color)" }}
                >
                  <ClipboardCheck className="w-5 h-5" />
                  Check-in de Alunos
                </button>

                <div className="flex items-center gap-3 p-3 border border-(--border-color) rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-(--bg-accent) flex items-center justify-center shrink-0">
                    <BookOpen className="w-5 h-5 text-(--text-secondary)" />
                  </div>
                  <div>
                    <span className="text-xs text-(--text-tertiary) block">
                      Escola
                    </span>
                    <span className="text-(--text-primary) font-medium">
                      {viewingActivity.school}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border border-(--border-color) rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-(--bg-accent) flex items-center justify-center shrink-0">
                    <BookOpen className="w-5 h-5 text-(--text-secondary)" />
                  </div>
                  <div>
                    <span className="text-xs text-(--text-tertiary) block">
                      Turma
                    </span>
                    <span className="text-(--text-primary) font-medium">
                      {viewingActivity.class}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-(--bg-secondary) flex justify-end gap-3 shrink-0">
              <button
                onClick={() => setViewingActivity(null)}
                className="px-4 py-2 text-(--text-secondary) font-medium hover:bg-(--bg-accent) rounded-lg transition"
              >
                Fechar
              </button>
              <button
                onClick={() => {
                  handleOpenModal(viewingActivity);
                  setViewingActivity(null);
                }}
                className="px-4 py-2 text-white font-medium rounded-lg transition hover:bg-orange-700 bg-orange-600"
              >
                Editar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Create/Edit Activity */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-(--card-bg) rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">
            <div
              className="px-6 py-4 border-b flex justify-between items-center shrink-0"
              style={{ borderColor: "var(--border-color)" }}
            >
              <h3 className="text-lg font-semibold text-(--text-primary)">
                {editingActivity ? "Editar Atividade" : "Nova Atividade"}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-(--text-tertiary) hover:text-(--text-secondary)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-(--text-secondary) mb-1">
                  Título da Atividade
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-(--border-color) rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-(--input-bg) text-(--text-primary)"
                  placeholder="Ex: Prova Mensal de Matemática"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-(--text-secondary) mb-1">
                    Matéria
                  </label>
                  <input
                    type="text"
                    list="subjects-list"
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-(--border-color) rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-(--input-bg) text-(--text-primary)"
                    placeholder="Ex: Matemática"
                  />
                  <datalist id="subjects-list">
                    {allAvailableSubjects.map((s) => (
                      <option key={`subject-${s}`} value={s} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-(--text-secondary) mb-1">
                    Tipo
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        type: e.target.value as ActivityData["type"],
                      })
                    }
                    className="w-full px-3 py-2 border border-(--border-color) rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-(--input-bg) text-(--text-primary)"
                  >
                    <option value="homework">Tarefa de Casa</option>
                    <option value="exam">Prova</option>
                    <option value="project">Trabalho</option>
                    <option value="review">Revisão</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-(--text-secondary) mb-2">
                  Estado da Atividade
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="radio"
                      name="status"
                      value="scheduled"
                      checked={formData.status === "scheduled"}
                      onChange={() =>
                        setFormData({ ...formData, status: "scheduled" })
                      }
                      className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                    />
                    <span
                      className={`text-sm ${formData.status === "scheduled" ? "text-orange-600 font-bold" : "text-(--text-secondary)"}`}
                    >
                      Agendada (Futuro)
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="radio"
                      name="status"
                      value="realized"
                      checked={formData.status === "realized"}
                      onChange={() =>
                        setFormData({ ...formData, status: "realized" })
                      }
                      className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                    />
                    <span
                      className={`text-sm ${formData.status === "realized" ? "text-orange-600 font-bold" : "text-(--text-secondary)"}`}
                    >
                      Já Realizada (Registro)
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-(--text-secondary) mb-1">
                    Turma
                  </label>
                  <select
                    value={formData.class_id}
                    onChange={(e) =>
                      setFormData({ ...formData, class_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-(--border-color) rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-(--input-bg) text-(--text-primary)"
                  >
                    <option value="">Selecione uma turma</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-(--text-secondary) mb-1">
                    {formData.status === "realized"
                      ? "Data da Atividade"
                      : "Data de Entrega"}
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--text-tertiary)" />
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) =>
                        setFormData({ ...formData, dueDate: e.target.value })
                      }
                      className="w-full pl-10 pr-3 py-2 border border-(--border-color) rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-(--input-bg) text-(--text-primary)"
                    />
                  </div>
                </div>
              </div>

              {formData.status === "realized" && formData.class_id && (
                <div className="mt-4 p-4 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-900/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <ClipboardCheck className="w-5 h-5 text-orange-600" />
                    <h4 className="font-bold text-orange-800 dark:text-orange-400">
                      Registro de Presença/Entrega
                    </h4>
                  </div>
                  <p className="text-sm text-orange-700 dark:text-orange-300 mb-2">
                    Após salvar, você poderá registrar a participação de cada
                    aluno.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-orange-600 dark:text-orange-500 italic">
                    <Check className="w-4 h-4" />O fluxo de check-in abrirá
                    automaticamente ao concluir.
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-(--bg-secondary) flex justify-end gap-3 shrink-0">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-(--text-secondary) font-medium hover:bg-(--bg-accent) rounded-lg transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={
                  !formData.title || !formData.class_id || !formData.subject
                }
                className="px-4 py-2 text-white font-medium rounded-lg transition hover:bg-orange-700 bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Salvar Atividade
              </button>
            </div>
          </div>
        </div>
      )}
      {isCheckInModalOpen && viewingActivity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-(--card-bg) rounded-xl shadow-xl w-full max-w-2xl h-[80vh] overflow-hidden animate-in fade-in zoom-in duration-200">
            <ActivityCheckIn
              activityId={viewingActivity.id}
              activityTitle={viewingActivity.title}
              classId={viewingActivity.class_id}
              className={viewingActivity.class || ""}
              onClose={() => setIsCheckInModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
